import { createBrowserRouter } from 'react-router-dom';
import { PlatformLayout } from '@/layouts/platform/PlatformLayout';
import { PlatformProtectedRoute } from '@/components/platform/PlatformProtectedRoute';
import { NotFoundPage } from '@/components/not-found';
import { PlatformLoginPage } from '@/features/platform/auth/PlatformLoginPage';
import { PlatformDashboardPage } from '@/features/platform/dashboard/PlatformDashboardPage';
import { CompaniesPage } from '@/features/platform/companies/CompaniesPage';
import { CompanyDetailPage } from '@/features/platform/companies/CompanyDetailPage';
import { CreateCompanyPage } from '@/features/platform/companies/CreateCompanyPage';
import { SubscriptionsPage } from '@/features/platform/subscriptions/SubscriptionsPage';
import { SubscriptionDetailPage } from '@/features/platform/subscriptions/SubscriptionDetailPage';
import { CommunicationHubPage } from '@/features/platform/communication/CommunicationHubPage';
import { PlatformUsersPage } from '@/features/platform/users/PlatformUsersPage';
import { MonitoringPage } from '@/features/platform/operations/MonitoringPage';
import { BackgroundJobsPage } from '@/features/platform/operations/BackgroundJobsPage';
import { AuditLogsPage } from '@/features/platform/operations/AuditLogsPage';
import { PlatformSettingsPage } from '@/features/platform/settings/PlatformSettingsPage';
import { PlatformProfilePage } from '@/features/platform/profile/PlatformProfilePage';
import { AssetDashboardPage } from '@/features/platform/storage/AssetDashboardPage';
import { AssetExplorerPage } from '@/features/platform/storage/AssetExplorerPage';
import { StorageProvidersPage } from '@/features/platform/storage/StorageProvidersPage';
import { AwsS3SetupGuidePage } from '@/features/platform/storage/AwsS3SetupGuidePage';
import { CloudflareR2SetupGuidePage } from '@/features/platform/storage/CloudflareR2SetupGuidePage';
import { StoragePoliciesPage } from '@/features/platform/storage/StoragePoliciesPage';
import { MigrationCenterPage } from '@/features/platform/storage/MigrationCenterPage';
import { ProviderMigrationsPage } from '@/features/platform/storage/ProviderMigrationsPage';
import { ProviderMigrationDetailPage } from '@/features/platform/storage/ProviderMigrationDetailPage';
import { StorageHealthPage } from '@/features/platform/storage/StorageHealthPage';
import { QuarantinePage } from '@/features/platform/storage/QuarantinePage';
import { StorageSettingsPage } from '@/features/platform/storage/StorageSettingsPage';
import { PLATFORM_ROUTES } from './platform-routes';

export const platformRouter = createBrowserRouter([
  {
    path: PLATFORM_ROUTES.LOGIN,
    element: <PlatformLoginPage />,
  },
  {
    path: '/platform',
    element: <PlatformLayout />,
    children: [
      {
        element: <PlatformProtectedRoute />,
        children: [
          { index: true, element: <PlatformDashboardPage /> },
          { path: PLATFORM_ROUTES.DASHBOARD, element: <PlatformDashboardPage /> },
          { path: PLATFORM_ROUTES.COMPANIES, element: <CompaniesPage /> },
          // BEFORE the :id route — "new" would otherwise match as an id and be
          // bounced by CompanyDetailPage's UUID guard.
          { path: PLATFORM_ROUTES.COMPANY_CREATE, element: <CreateCompanyPage /> },
          { path: PLATFORM_ROUTES.COMPANY_DETAIL, element: <CompanyDetailPage /> },
          { path: PLATFORM_ROUTES.SUBSCRIPTIONS, element: <SubscriptionsPage /> },
          { path: PLATFORM_ROUTES.SUBSCRIPTION_DETAIL, element: <SubscriptionDetailPage /> },
          { path: PLATFORM_ROUTES.COMMUNICATION, element: <CommunicationHubPage /> },
          { path: PLATFORM_ROUTES.COMMUNICATION_TEMPLATES, element: <CommunicationHubPage /> },
          { path: PLATFORM_ROUTES.COMMUNICATION_GATEWAYS, element: <CommunicationHubPage /> },
          { path: PLATFORM_ROUTES.COMMUNICATION_LOGS, element: <CommunicationHubPage /> },
          { path: PLATFORM_ROUTES.PLATFORM_USERS, element: <PlatformUsersPage /> },
          { path: PLATFORM_ROUTES.OPERATIONS_MONITORING, element: <MonitoringPage /> },
          { path: PLATFORM_ROUTES.OPERATIONS_JOBS, element: <BackgroundJobsPage /> },
          { path: PLATFORM_ROUTES.OPERATIONS_AUDIT, element: <AuditLogsPage /> },
          { path: PLATFORM_ROUTES.STORAGE_DASHBOARD, element: <AssetDashboardPage /> },
          { path: PLATFORM_ROUTES.STORAGE_ASSETS, element: <AssetExplorerPage /> },
          { path: PLATFORM_ROUTES.STORAGE_PROVIDERS, element: <StorageProvidersPage /> },
          {
            path: PLATFORM_ROUTES.STORAGE_PROVIDER_AWS_GUIDE,
            element: <AwsS3SetupGuidePage />,
          },
          {
            path: PLATFORM_ROUTES.STORAGE_PROVIDER_R2_GUIDE,
            element: <CloudflareR2SetupGuidePage />,
          },
          { path: PLATFORM_ROUTES.STORAGE_POLICIES, element: <StoragePoliciesPage /> },
          { path: PLATFORM_ROUTES.STORAGE_MIGRATION, element: <MigrationCenterPage /> },
          {
            path: PLATFORM_ROUTES.STORAGE_PROVIDER_MIGRATIONS,
            element: <ProviderMigrationsPage />,
          },
          {
            path: `${PLATFORM_ROUTES.STORAGE_PROVIDER_MIGRATIONS}/:id`,
            element: <ProviderMigrationDetailPage />,
          },
          { path: PLATFORM_ROUTES.STORAGE_HEALTH, element: <StorageHealthPage /> },
          { path: PLATFORM_ROUTES.STORAGE_QUARANTINE, element: <QuarantinePage /> },
          { path: PLATFORM_ROUTES.STORAGE_SETTINGS, element: <StorageSettingsPage /> },
          { path: PLATFORM_ROUTES.SETTINGS, element: <PlatformSettingsPage /> },
          { path: PLATFORM_ROUTES.PROFILE, element: <PlatformProfilePage /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <div className="flex h-screen items-center justify-center bg-background">
        <NotFoundPage />
      </div>
    ),
  },
]);
